import { useState, useEffect } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Search, Plus, ChevronLeft, ChevronRight, Users, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const Clientes = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', codigo: '', contacto: '', telefono: '', email: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { canCreate, canUpdate, canDelete } = usePermissions('config', 'clientes');

  // Sorting
  const [sortField, setSortField] = useState('nombre');
  const [sortOrder, setSortOrder] = useState('asc');
  const sortedClientes = [...clientes].sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase();
    const bVal = (b[sortField] || '').toString().toLowerCase();
    return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
  const handleSort = (field) => {
    if (sortField === field) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pagination.page, limit: pagination.limit });
      if (search && search.length >= 2) params.append('search', search);
      const response = await api.get(`/config/clientes?${params}`);
      setClientes(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) { toast.error('Error al cargar clientes'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [pagination.page]);
  useEffect(() => { 
    const t = setTimeout(() => { 
      if (search === '' || search.length >= 2) { 
        setPagination(prev => ({ ...prev, page: 1 })); 
        fetchData(); 
      } 
    }, 800); 
    return () => clearTimeout(t); 
  }, [search]);

  const handleCreate = () => { 
    setEditingItem(null); 
    setFormData({ nombre: '', codigo: '', contacto: '', telefono: '', email: '' }); 
    setModalOpen(true); 
  };
  
  const handleEdit = (item) => { 
    setEditingItem(item); 
    setFormData({ 
      nombre: item.nombre || '', 
      codigo: item.codigo || '', 
      contacto: item.contacto || '', 
      telefono: item.telefono || '', 
      email: item.email || '' 
    }); 
    setModalOpen(true); 
  };
  
  const handleSave = async () => {
    if (!formData.nombre.trim()) { toast.error('El nombre es requerido'); return; }
    try {
      if (editingItem) { 
        await api.put(`/config/clientes/${editingItem.id}`, formData); 
        toast.success('Cliente actualizado'); 
      } else { 
        await api.post('/config/clientes', formData); 
        toast.success('Cliente creado'); 
      }
      setModalOpen(false); 
      fetchData();
    } catch (error) { toast.error('Error al guardar'); }
  };
  
  const handleDelete = async (id) => { 
    try { 
      await api.delete(`/config/clientes/${id}`); 
      toast.success('Cliente eliminado'); 
      setDeleteConfirm(null); 
      fetchData(); 
    } catch (error) { toast.error('Error al eliminar'); } 
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Clientes</h1>
            <p className="page-subtitle">Gestion de clientes externos</p>
          </div>
          {canCreate && (
            <Button className="btn btn-primary" onClick={handleCreate} data-testid="add-cliente-btn">
              <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nuevo Cliente
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stats-card">
            <div className="mb-2"><Users className="h-8 w-8 text-primary" strokeWidth={1.5} /></div>
            <div className="stats-card-value">{pagination.total}</div>
            <div className="stats-card-label">Clientes activos</div>
          </div>
        </div>

        {/* Table Card */}
        <Card className="card-subtle overflow-hidden">
          <div className="p-4 border-b border-border/30">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              <Input
                placeholder="Buscar cliente..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/50" onClick={() => handleSort('nombre')}>
                    <div className="flex items-center gap-1">NOMBRE <SortIcon field="nombre" /></div>
                  </th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/50" onClick={() => handleSort('codigo')}>
                    <div className="flex items-center gap-1">CODIGO <SortIcon field="codigo" /></div>
                  </th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground hidden md:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('contacto')}>
                    <div className="flex items-center gap-1">CONTACTO <SortIcon field="contacto" /></div>
                  </th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground hidden lg:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('telefono')}>
                    <div className="flex items-center gap-1">TELEFONO <SortIcon field="telefono" /></div>
                  </th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground hidden lg:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('email')}>
                    <div className="flex items-center gap-1">EMAIL <SortIcon field="email" /></div>
                  </th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : sortedClientes.length === 0 ? (
                  <tr><td colSpan={6} className="text-center p-8 text-muted-foreground">No se encontraron clientes</td></tr>
                ) : (
                  sortedClientes.map(cliente => (
                    <tr key={cliente.id} className="border-b hover:bg-muted/30">
                      <td className="p-3 font-medium">{cliente.nombre}</td>
                      <td className="p-3">{cliente.codigo || '-'}</td>
                      <td className="p-3 hidden md:table-cell">{cliente.contacto || '-'}</td>
                      <td className="p-3 hidden lg:table-cell">{cliente.telefono || '-'}</td>
                      <td className="p-3 hidden lg:table-cell">{cliente.email || '-'}</td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canUpdate && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(cliente)} title="Editar" data-testid={`edit-cliente-${cliente.id}`}>
                              <Edit className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeleteConfirm(cliente)} title="Eliminar" data-testid={`delete-cliente-${cliente.id}`}>
                              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t">
            <div className="text-sm text-muted-foreground">
              Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1}>
                <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
              </Button>
              <span className="text-sm">Pagina {pagination.page} de {pagination.pages || 1}</span>
              <Button variant="outline" size="sm" onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page >= pagination.pages}>
                <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-1">
                <Label className="text-xs">Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Nombre del cliente"
                  data-testid="input-nombre"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Codigo</Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData(prev => ({ ...prev, codigo: e.target.value }))}
                  placeholder="Codigo interno"
                  data-testid="input-codigo"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Contacto</Label>
                <Input
                  value={formData.contacto}
                  onChange={(e) => setFormData(prev => ({ ...prev, contacto: e.target.value }))}
                  placeholder="Persona de contacto"
                  data-testid="input-contacto"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Telefono</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                  placeholder="Telefono"
                  data-testid="input-telefono"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@ejemplo.com"
                  data-testid="input-email"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} data-testid="submit-form">{editingItem ? 'Guardar' : 'Crear'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Eliminacion</DialogTitle>
            </DialogHeader>
            <p className="py-4">¿Esta seguro de eliminar el cliente <strong>{deleteConfirm?.nombre}</strong>?</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete">Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Clientes;
